import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../shared/api';
import { formatMoney } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { Breadcrumb } from '../shared/Breadcrumb';
import type { ModuleKey } from '../shared/biModuleRegistry';
import { hasModuleGrant } from '../shared/biModuleKeys';

type Locale = 'hr' | 'en';

/** Landing modul 40 — kartice koje vode na 40.1 i 40.2 */
export function WarehouseLanding({ locale, userEmail, user, onNavigateSub }: { locale: Locale; userEmail?: string; user: { allowed_modules?: string[] }; onNavigateSub: (sub: ModuleKey) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const showRoadmap = (userEmail ?? '').toLowerCase() === 'vjakomin@gmail.com';
    // ACL: kartica se prikazuje samo ako user ima grant na taj podmodul (superadmin vidi sve).
    const canSub = (k: ModuleKey) => hasModuleGrant(user, k);
    // 40.63 se otvara i s pravom na 40.61 (Vedran 12.10.2026: novi modul, isti krug ljudi).
    // 40.43: vide ga svi koji vide neki 40.x (Vedran 21.10.2026).
    const canRestock = user.allowed_modules === undefined || user.allowed_modules.some((k) => k === 'warehouse' || k.startsWith('warehouse_'));
    const canV3 = canSub('warehouse_coverage_v3') || canSub('warehouse_coverage_v2');
    const regularSubs = (['warehouse_coverage_v3', 'warehouse_gold_cm', 'warehouse_summary_orders', 'warehouse_stock', 'warehouse_plan_wizard', 'warehouse_gold_coverage', 'warehouse_coverage_v2', 'warehouse_exposure_v2', 'warehouse_daily_inventory', 'warehouse_lager_plan', 'warehouse_insurance', 'warehouse_overview', 'warehouse_restock_prep', 'warehouse_branch_restock', 'warehouse_import_orders', 'warehouse_tca_invoices', 'warehouse_gtm_scrap', 'warehouse_plan_generate_v2'] as ModuleKey[]).filter((k) => (k === 'warehouse_coverage_v3' ? canV3 : k === 'warehouse_restock_prep' || k === 'warehouse_branch_restock' || k === 'warehouse_import_orders' || k === 'warehouse_tca_invoices' || k === 'warehouse_gtm_scrap' ? canRestock : canSub(k)));
    const parentGranted = hasModuleGrant(user, 'warehouse');
    // Auto-jump: user BEZ parent granta s točno jednim podmodulom → preskoči landing
    // (npr. poslovnica sa samo 40.3 ide ravno u Skladište, kao i prije).
    useEffect(() => {
        if (!parentGranted && regularSubs.length === 1) onNavigateSub(regularSubs[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🏭</div>
                <div className="bi-module-hero-text">
                    <h1>{t('40 · Skladišno poslovanje', '40 · Warehouse')}</h1>
                    <p className="muted">{t('Operativa skladišta — Gold Category Manager i Zbirni nalozi preskladištenja.', 'Warehouse ops — Gold Category Manager and stock transfer summary orders.')}</p>
                </div>
            </div>
            {canV3 ? (
                // Vedran 12.10.2026: 40.63 istaknut u sredini prikaza, ostali moduli manji ispod.
                <div style={{ display: 'flex', justifyContent: 'center', margin: '1.25rem 0 1.5rem' }}>
                    <button
                        type="button"
                        className="card admin-card"
                        style={{ textAlign: 'center', cursor: 'pointer', border: '2px solid #475569', padding: '2rem 2.5rem', width: 'min(720px, 100%)', background: 'linear-gradient(135deg, #f8fafc 0%, rgba(226,232,240,0.6) 100%)' }}
                        onClick={() => onNavigateSub('warehouse_coverage_v3')}
                    >
                        <p className="eyebrow">40.63 · treasury</p>
                        <h2 style={{ margin: '0.5rem 0', fontSize: '1.9rem' }}>⚖️ {t('Pokrivenost zlata V3', 'Gold coverage V3')}</h2>
                        <p className="muted" style={{ fontSize: '1rem', margin: 0 }}>
                            {t(
                                'Pokrivenost narudzbi S1-S5, lager L1-L5, akcijsko, komisija, GG SLO, GTM i razlika H1-H4. Z1, Z1b i Z1-V2, izbornik zemlje.',
                                'Order coverage S1-S5, lager L1-L5, promo, consignment, GG SLO, GTM and gap H1-H4. Z1, Z1b and Z1-V2, country selector.'
                            )}
                        </p>
                    </button>
                </div>
            ) : null}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
                {canSub('warehouse_gold_cm') ? (
                <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem' }} onClick={() => onNavigateSub('warehouse_gold_cm')}>
                    <p className="eyebrow">40.1</p>
                    <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>Gold Category Manager</h3>
                    <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Spot cijene i revenue management paketi.', 'Spot prices and revenue management packages.')}</p>
                </button>
                ) : null}
                {canSub('warehouse_summary_orders') ? (
                <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem' }} onClick={() => onNavigateSub('warehouse_summary_orders')}>
                    <p className="eyebrow">40.2</p>
                    <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>{t('Zbirni nalozi', 'Summary orders')}</h3>
                    <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Mass upload PDF + email ingestion + zbrojni nalog za printanje.', 'Mass upload + email ingestion + printable summary.')}</p>
                </button>
                ) : null}
                {canSub('warehouse_stock') ? (
                <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem' }} onClick={() => onNavigateSub('warehouse_stock')}>
                    <p className="eyebrow">40.3</p>
                    <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>{t('Skladište', 'Inventory')}</h3>
                    <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Akcijsko + komisijsko stanje, rezervacije poslovnica, ERP otprema — paralelno dok ne stigne FAROS.', 'Promotional + consignment stock, branch reservations, ERP dispatch — parallel until FAROS.')}</p>
                </button>
                ) : null}
                {canSub('warehouse_plan_wizard') ? (
                <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem' }} onClick={() => onNavigateSub('warehouse_plan_wizard')}>
                    <p className="eyebrow">40.4</p>
                    <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🚚 Plan & generate</h3>
                    <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Vođeni wizard: filtri/skladišta → narudžbe → isporuke / preskladištenja / narudžbe dobavljačima (PDF+XLS).', 'Guided wizard: filters/warehouses → orders → deliveries / restocking / supplier orders (PDF+XLS).')}</p>
                </button>
                ) : null}
                {regularSubs.length === 0 && !showRoadmap ? (
                    <p className="muted" style={{ gridColumn: '1 / -1', fontStyle: 'italic' }}>{t('Nemaš pristup nijednom podmodulu skladišta — javi se administratoru.', 'No access to any warehouse sub-module — contact your administrator.')}</p>
                ) : null}
                {showRoadmap ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_plan_generate')}>
                        <p className="eyebrow">40.41 · {t('interno', 'internal')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🚚 Plan & generate ({t('stari', 'legacy')})</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Stari PHASED planer — referenca dok se novi wizard gradi. Vidljivo samo tebi.', 'Legacy PHASED planner — reference while the new wizard is built. Visible only to you.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_plan_generate_v2') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_plan_generate_v2')}>
                        <p className="eyebrow">40.42</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🚚 Plan & generate V2</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('PG1 (narudžbe klijenata) + PG2 (narudžba za lager) — novi wizard, u izgradnji.', 'PG1 (client orders) + PG2 (warehouse order) — new wizard, in progress.')}</p>
                    </button>
                ) : null}
                {showRoadmap ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_roadmap')}>
                        <p className="eyebrow">40.5 · {t('interno', 'internal')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🗺️ {t('Roadmap & pravila', 'Roadmap & rules')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Pravila, logika, otvorene rupe i status implementacije modula 40 stack-a. Vidljivo samo tebi.', 'Rules, logic, open gaps and implementation status. Visible only to you.')}</p>
                    </button>
                ) : null}
                {showRoadmap ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #eef2ff 0%, rgba(224,231,255,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_gap_report')}>
                        <p className="eyebrow">40.5.1 · treasury</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>📊 {t('GAP izvještaj', 'GAP report')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Tjedni GAP (pokriće) — upload, trend i usporedba s BIS izračunom. Vidljivo samo tebi.', 'Weekly GAP (coverage) — upload, trend and reconciliation. Visible only to you.')}</p>
                    </button>
                ) : null}
                {showRoadmap ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #ecfdf5 0%, rgba(209,250,229,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_gap_auto')}>
                        <p className="eyebrow">40.5.2 · treasury</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🤖 {t('GAP auto-izračun', 'GAP auto-compute')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('BIS auto GAP iz živih podataka + checklist što fali. Vidljivo samo tebi.', 'BIS auto GAP from live data + missing checklist. Visible only to you.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_gold_coverage') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #fef3c7 0%, rgba(253,230,138,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_gold_coverage')}>
                        <p className="eyebrow">40.6 · treasury</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>⚖️ {t('Pokrivenost zlatom', 'Gold coverage')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Zaliha, roba na putu i neisporučene narudžbe, X = (1+2+3+6a+6b)−5; roba kod dobavljača (4) samo u Y.', 'Stock, goods in transit and undelivered orders, X = (1+2+3+6a+6b)−5; supplier goods (4) only in Y.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_overview') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_overview')}>
                        <p className="eyebrow">40.31 · {t('pregled', 'overview')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>📋 {t('Pregled skladišta i narudžbi', 'Stock and orders overview')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Zaliha, neisporučeno po artiklu i veličini, otvorene narudžbe po danu. Poslovnica, regija, maloprodaja, država.', 'Stock, undelivered by item and size, open orders per day. Branch, region, retail, country.')}</p>
                    </button>
                ) : null}
                {canRestock ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_restock_prep')}>
                        <p className="eyebrow">40.43 · {t('preskladištenje', 'restocking')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🚚 {t('Preskladištenje, priprema', 'Restocking preparation')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Otvorene narudžbe sa zalihom na 212 i 210, grupno označavanje "na putu" (vozač, HP, GLS) i izvoz u Excel.', 'Open orders with stock on 212 and 210, bulk "in transit" marking (driver, HP, GLS) and Excel export.')}</p>
                    </button>
                ) : null}
                {canRestock ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_branch_restock')}>
                        <p className="eyebrow">40.44 · {t('dopuna lagera', 'replenishment')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>📦 {t('Dopuna lagera, priprema', 'Branch stock replenishment')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Prijedlog preskladištenja sa 214 u poslovnice po asortimanu MIN/MAX, Excel i oznake pakirano / poslano.', 'Transfer proposal from 214 to branches by assortment MIN/MAX, Excel and packed / sent marking.')}</p>
                    </button>
                ) : null}
                {canRestock ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_import_orders')}>
                        <p className="eyebrow">40.45 · {t('ino dobavljači', 'foreign suppliers')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🧾 {t('Ino narudžbe i računi', 'Foreign supplier orders and invoices')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Narudžbe i računi ino dobavljača, plaćanja, dolasci robe i prijevoz, s vezom na Plan & Generate i Cashflow.', 'Foreign supplier orders and invoices, payments, arrivals and transport, linked to Plan & Generate and Cashflow.')}</p>
                    </button>
                ) : null}
                {canRestock ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_tca_invoices')}>
                        <p className="eyebrow">40.46 · {t('TCA', 'TCA')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🏭 {t('Računi TCA', 'TCA invoices')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Ponude TCA od avansa do finalne fakture, troškovi obrade, razlika nakon analize i dug TCA.', 'TCA offers from advance to final invoice, processing costs, post-analysis difference and TCA balance.')}</p>
                    </button>
                ) : null}
                {canRestock ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_gtm_scrap')}>
                        <p className="eyebrow">40.47 · {t('lom', 'scrap')}</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>♻️ {t('Lom GTM', 'GTM scrap')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Otpremnice loma iz Plovdiva i Sofije, pošiljke prema TCA s odstupanjem fine mase i računi GTM.', 'Scrap delivery notes from Plovdiv and Sofia, TCA consignments with fine deviation and GTM invoices.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_coverage_v2') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_coverage_v2')}>
                        <p className="eyebrow">40.61 · treasury</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>⚖️ {t('Pokrivenost zlatom V2', 'Gold coverage V2')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('S1-S4, T1, lager plan/stanje, I1, Z1 i Z1b — semafor i dani pokrića.', 'S1-S4, T1, lager plan/actual, I1, Z1 and Z1b — traffic light and days of cover.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_exposure_v2') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem', background: 'linear-gradient(135deg, #f1f5f9 0%, rgba(226,232,240,0.4) 100%)' }} onClick={() => onNavigateSub('warehouse_exposure_v2')}>
                        <p className="eyebrow">40.62 · treasury</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🌍 {t('Ukupna izloženost zlatu', 'Total gold exposure')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Nadskup 40.61 — P1/P2 pohrana, V1-V4 zlato izvan lagera, H6 StoneX trading, zbrojni Z2.', 'Superset of 40.61 — P1/P2 custody, V1-V4 gold outside the lager, H2 StoneX trading, total Z2.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_daily_inventory') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem' }} onClick={() => onNavigateSub('warehouse_daily_inventory')}>
                        <p className="eyebrow">40.7</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>📋 {t('Dnevna inventura', 'Daily inventory count')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Popis viškova/manjkova po skladištu — unos, pregled, odobrenje, knjiženje.', 'Warehouse surplus/shortage count — entry, review, approval, posting.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_lager_plan') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem' }} onClick={() => onNavigateSub('warehouse_lager_plan')}>
                        <p className="eyebrow">40.8</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🧱 {t('Planiranje lagera', 'Lager planning')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Matrica poslovnica × apoen, odobravanje plana i politika akcijskog skladišta.', 'Branch × denomination matrix, plan approval and promo warehouse policy.')}</p>
                    </button>
                ) : null}
                {canSub('warehouse_insurance') ? (
                    <button type="button" className="card admin-card" style={{ textAlign: 'left', cursor: 'pointer', border: 0, padding: '0.8rem' }} onClick={() => onNavigateSub('warehouse_insurance')}>
                        <p className="eyebrow">40.9</p>
                        <h3 style={{ margin: '0.3rem 0', fontSize: '1rem' }}>🛡️ {t('Osiguranje zaliha poslovnica', 'Branch stock insurance')}</h3>
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>{t('Dnevna usporedba zaliha s osiguranom svotom, prijedlog prijave i dopis osiguranju.', 'Daily stock vs insured sum comparison, declaration proposal and insurer letter.')}</p>
                    </button>
                ) : null}
            </div>
        </section>
    );
}

type WarehouseOrder = {
    id: number; public_code: string; source: string; source_filename: string | null;
    document_date: string | null; warehouse_from_code: string | null; warehouse_from_name: string | null;
    warehouse_to_code: string | null; warehouse_to_name: string | null;
    order_number: string | null; total_amount_eur: number; total_units: number; distinct_products: number;
    status: string; summary_order_id: number | null; has_errors: number; errors: string | null;
    created_at: string;
    // F2: fizicko kretanje robe, neovisno o `status` (koji opisuje obradu PDF-a).
    shipped_at?: string | null; received_at?: string | null;
    shipped_by_name?: string | null; received_by_name?: string | null;
};

/** F2: zbroj robe na putu po smjeru, iz /admin/warehouse-orders/transit. */
type TransitLeg = {
    label: string; qty: number; count: number;
    by_layer: Record<string, number>; by_source: Record<string, number>;
    duplicates_removed: number;
};
type TransitSummary = {
    outbound: TransitLeg; inbound: TransitLeg;
    reconcile: {
        snapshot_date: string | null; faros_qty_in_transit: number | null;
        bis_outbound_qty: number; bis_inbound_qty: number; bis_total_qty: number; difference: number | null;
    };
};

type Summary = { id: number; public_code: string; created_at: string; status: string; total_units: number; distinct_products: number; orders_count: number; printed_at?: string | null; printed_by_name?: string | null };

export function WarehouseSummaryOrdersModule({ locale, token, isSuperadmin = false, onBack }: { locale: Locale; token: string; isSuperadmin?: boolean; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [pending, setPending] = useState<WarehouseOrder[]>([]);
    const [summaries, setSummaries] = useState<Summary[]>([]);
    const [selected, setSelected] = useState<Record<number, boolean>>({});
    const [showUpload, setShowUpload] = useState(false);
    const [showSummariesList, setShowSummariesList] = useState(false);
    const [summariesQuery, setSummariesQuery] = useState('');
    const [summariesPage, setSummariesPage] = useState(1);
    const SUMMARIES_PER_PAGE = 10;
    const [drilldown, setDrilldown] = useState<{ summary: Summary; orders: any[]; items: any[] } | null>(null);
    const [orderDetail, setOrderDetail] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fetchingEmail, setFetchingEmail] = useState(false);
    const [rematching, setRematching] = useState(false);
    const [emailResult, setEmailResult] = useState<{ ok: boolean; output: string } | null>(null);
    const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
    // F2 — roba na putu.
    const [transit, setTransit] = useState<TransitSummary | null>(null);
    const [transitTab, setTransitTab] = useState<'not_shipped' | 'on_the_way'>('not_shipped');
    const [transitOrders, setTransitOrders] = useState<WarehouseOrder[]>([]);
    const [transitBusy, setTransitBusy] = useState(false);
    const [showTransit, setShowTransit] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [p, s] = await Promise.all([
                api<{ rows: WarehouseOrder[] }>('/admin/warehouse-orders?status=pending', token),
                api<{ rows: Summary[] }>('/admin/warehouse-summaries', token),
            ]);
            setPending(p.rows);
            setSummaries(s.rows);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Učitavanje nije uspjelo.');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void reload(); }, [reload]);

    /** F2: zbroj po smjeru + lista naloga za oznacavanje otpreme/zaprimanja. */
    const reloadTransit = useCallback(async (tab: 'not_shipped' | 'on_the_way') => {
        setTransitBusy(true);
        try {
            const [sum, list] = await Promise.all([
                api<TransitSummary>('/admin/warehouse-orders/transit', token),
                api<{ rows: WarehouseOrder[] }>(`/admin/warehouse-orders?status=all&transit=${tab}&limit=200`, token),
            ]);
            setTransit(sum);
            setTransitOrders(list.rows);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Učitavanje robe na putu nije uspjelo.');
        } finally {
            setTransitBusy(false);
        }
    }, [token]);

    useEffect(() => { if (showTransit) void reloadTransit(transitTab); }, [showTransit, transitTab, reloadTransit]);

    const markTransit = useCallback(async (id: number, action: 'mark-shipped' | 'mark-received' | 'undo-transit') => {
        try {
            await api(`/admin/warehouse-orders/${id}/${action}`, token, { method: 'POST' });
            await reloadTransit(transitTab);
        } catch (e) {
            setError(e instanceof Error ? e.message : '');
        }
    }, [token, transitTab, reloadTransit]);

    const selectedIds = useMemo(() => Object.entries(selected).filter(([, v]) => v).map(([k]) => Number(k)), [selected]);

    return (
        <section className="card admin-block">
            <Breadcrumb items={[
                { label: t('🏭 40 · Skladišno poslovanje', '🏭 40 · Warehouse'), onClick: onBack },
                { label: t('40.2 · Zbirni nalozi', '40.2 · Summary orders') },
            ]} />
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📋</div>
                <div className="bi-module-hero-text">
                    <h1>{t('40.2 · Zbirni nalozi', '40.2 · Summary orders')}</h1>
                    <p className="muted">{t('Mass upload PDF dokumenata preskladištenja ili automatski preko email ingestion-a (preskladistenje@aurodomus.eu, svaki minute).', 'Mass PDF upload or email ingestion (every minute).')}</p>
                </div>
                <button type="button" className="secondary compact" disabled={loading} onClick={() => void reload()} title={t('Osvježi liste bez reload-a stranice', 'Reload lists')}>
                    {loading ? '⏳' : '🔄'} {t('Osvježi', 'Refresh')}
                </button>
                {isSuperadmin ? (
                    <button type="button" className="secondary compact" disabled={rematching} title={t('Ponovno upari stavke s katalogom + počisti zastarjele greške (superadmin)', 'Re-match items + clear stale errors (superadmin)')}
                        onClick={async () => {
                            setRematching(true); setError('');
                            try {
                                const r = await api<{ ok: boolean; items_rematched: number; orders_cleared: number; orders_still_with_errors: number }>('/admin/warehouse-orders/rematch', token, { method: 'POST' });
                                setEmailResult({ ok: true, output: t(`Re-match: ${r.items_rematched} stavki upareno, ${r.orders_cleared} naloga očišćeno, ${r.orders_still_with_errors} još s greškom.`, `Re-match: ${r.items_rematched} items, ${r.orders_cleared} cleared, ${r.orders_still_with_errors} still with errors.`) });
                                await reload();
                            } catch (e) { setError(e instanceof Error ? e.message : 'Re-match fail'); }
                            finally { setRematching(false); }
                        }}>
                        {rematching ? '⏳' : '🔁'} {t('Re-match', 'Re-match')}
                    </button>
                ) : null}
            </div>

            {error ? <p className="error">{error}</p> : null}

            {/* Beautiful action card toolbar */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: selectedIds.length > 0 ? 'repeat(auto-fit, minmax(220px, 1fr))' : 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '0.9rem',
                    marginBottom: '1.2rem',
                }}
            >
                {/* Mass upload card */}
                <button
                    type="button"
                    onClick={() => setShowUpload(true)}
                    style={{
                        textAlign: 'left',
                        background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.55) 100%)',
                        border: '1.5px solid rgba(var(--ad-soft-rgb),0.35)',
                        borderRadius: 14,
                        padding: '1rem 1.1rem',
                        cursor: 'pointer',
                        transition: 'all 160ms ease',
                        boxShadow: '0 1px 3px rgba(107,79,28,0.06)',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(var(--ad-acc-rgb),0.18)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(107,79,28,0.06)'; }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 4 }}>
                        <span style={{ fontSize: '1.6rem' }}>📥</span>
                        <strong style={{ color: 'var(--ad-ink)', fontSize: '0.98rem' }}>{t('Mass upload', 'Mass upload')}</strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.35 }}>
                        {t('Učitaj PDF naloge sa diska — drag & drop podržan.', 'Upload PDF orders from disk — drag & drop supported.')}
                    </p>
                </button>

                {/* Dohvati email card */}
                <button
                    type="button"
                    disabled={fetchingEmail}
                    onClick={async () => {
                        setFetchingEmail(true);
                        setEmailResult(null);
                        try {
                            const r = await api<{ ok: boolean; output: string; exit_code: number }>('/admin/warehouse-orders/fetch-email', token, { method: 'POST' });
                            setEmailResult({ ok: r.ok, output: r.output ?? '' });
                            await reload();
                        } catch (e) {
                            setEmailResult({ ok: false, output: e instanceof Error ? e.message : 'fail' });
                        } finally {
                            setFetchingEmail(false);
                        }
                    }}
                    style={{
                        textAlign: 'left',
                        background: fetchingEmail
                            ? 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                            : 'linear-gradient(135deg, #eff6ff 0%, rgba(219,234,254,0.55) 100%)',
                        border: '1.5px solid rgba(59,130,246,0.28)',
                        borderRadius: 14,
                        padding: '1rem 1.1rem',
                        cursor: fetchingEmail ? 'wait' : 'pointer',
                        transition: 'all 160ms ease',
                        boxShadow: '0 1px 3px rgba(30,64,175,0.06)',
                        opacity: fetchingEmail ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => { if (! fetchingEmail) { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(59,130,246,0.18)'; } }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(30,64,175,0.06)'; }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 4 }}>
                        <span style={{ fontSize: '1.6rem' }}>{fetchingEmail ? '⏳' : '📧'}</span>
                        <strong style={{ color: '#1e40af', fontSize: '0.98rem' }}>{fetchingEmail ? t('Dohvaćam…', 'Fetching…') : t('Dohvati email', 'Fetch email')}</strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.35 }}>
                        {t('Ručno povuci nove naloge iz preskladistenje@aurodomus.eu.', 'Manually pull new orders from inbox.')}
                    </p>
                </button>

                {/* Prethodni zbirni nalozi card */}
                <button
                    type="button"
                    onClick={() => setShowSummariesList((v) => !v)}
                    style={{
                        textAlign: 'left',
                        background: showSummariesList
                            ? 'linear-gradient(135deg, #f0fdf4 0%, rgba(220,252,231,0.55) 100%)'
                            : 'linear-gradient(135deg, #f8fafc 0%, rgba(241,245,249,0.55) 100%)',
                        border: showSummariesList ? '1.5px solid rgba(31,122,54,0.35)' : '1.5px solid rgba(148,163,184,0.32)',
                        borderRadius: 14,
                        padding: '1rem 1.1rem',
                        cursor: 'pointer',
                        transition: 'all 160ms ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        position: 'relative',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 16px rgba(31,122,54,0.14)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 4 }}>
                        <span style={{ fontSize: '1.6rem' }}>📋</span>
                        <strong style={{ color: showSummariesList ? '#1f7a36' : '#334155', fontSize: '0.98rem', flex: 1 }}>{t('Prethodni zbirni nalozi', 'Previous summaries')}</strong>
                        <span style={{
                            background: showSummariesList ? '#1f7a36' : '#94a3b8',
                            color: '#fff',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: 10,
                            minWidth: 26,
                            textAlign: 'center',
                        }}>{summaries.length}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.35 }}>
                        {showSummariesList ? t('Sakrij listu.', 'Hide list.') : t('Pregled, print i poništavanje arhive.', 'Browse, print and cancel archive.')}
                    </p>
                </button>

                {/* Kreiraj zbirni nalog — uvjetna kartica */}
                {selectedIds.length > 0 ? (
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                const r = await api<{ id: number; public_code: string }>('/admin/warehouse-summaries', token, { method: 'POST', body: JSON.stringify({ order_ids: selectedIds }) });
                                setSelected({});
                                await reload();
                                alert(t('Kreiran zbirni nalog: ', 'Summary created: ') + r.public_code);
                            } catch (e) { setError(e instanceof Error ? e.message : ''); }
                        }}
                        style={{
                            textAlign: 'left',
                            background: 'linear-gradient(135deg, #1f7a36 0%, #15803d 100%)',
                            border: '1.5px solid rgba(31,122,54,0.5)',
                            borderRadius: 14,
                            padding: '1rem 1.1rem',
                            cursor: 'pointer',
                            transition: 'all 160ms ease',
                            boxShadow: '0 4px 12px rgba(31,122,54,0.28)',
                            color: '#fff',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 22px rgba(31,122,54,0.35)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(31,122,54,0.28)'; }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 4 }}>
                            <span style={{ fontSize: '1.6rem' }}>✅</span>
                            <strong style={{ fontSize: '0.98rem', flex: 1 }}>{t('Kreiraj zbirni nalog', 'Create summary')}</strong>
                            <span style={{
                                background: 'rgba(255,255,255,0.25)',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: 10,
                                minWidth: 26,
                                textAlign: 'center',
                            }}>{selectedIds.length}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.35 }}>
                            {t('Spoji odabrane naloge u jedan zbirni za printanje.', 'Merge selected orders into a printable summary.')}
                        </p>
                    </button>
                ) : null}
            </div>

            {/* Email fetch result banner */}
            {emailResult ? (
                <div style={{
                    padding: '0.7rem 0.9rem',
                    marginBottom: '1rem',
                    borderRadius: 10,
                    background: emailResult.ok ? 'rgba(31,122,54,0.08)' : 'rgba(177,64,64,0.08)',
                    border: emailResult.ok ? '1px solid rgba(31,122,54,0.25)' : '1px solid rgba(177,64,64,0.25)',
                    fontSize: '0.85rem',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ color: emailResult.ok ? '#1f7a36' : '#b14040' }}>
                            {emailResult.ok ? '✓' : '✗'} {emailResult.ok ? t('Email fetch dovršen', 'Email fetch complete') : t('Email fetch greška', 'Email fetch failed')}
                        </strong>
                        <button type="button" onClick={() => setEmailResult(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer', color: 'rgba(0,0,0,0.5)', fontSize: '1.1rem', fontWeight: 700 }}>×</button>
                    </div>
                    {emailResult.output ? (
                        <pre style={{ margin: '6px 0 0', fontSize: '0.74rem', color: 'rgba(0,0,0,0.7)', maxHeight: 140, overflowY: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{emailResult.output.trim()}</pre>
                    ) : null}
                </div>
            ) : null}

            {showSummariesList ? (() => {
                const q = summariesQuery.trim().toLowerCase();
                const filtered = q
                    ? summaries.filter((s) => s.public_code.toLowerCase().includes(q) || (s.status ?? '').toLowerCase().includes(q) || (s.printed_by_name ?? '').toLowerCase().includes(q))
                    : summaries;
                const totalPages = Math.max(1, Math.ceil(filtered.length / SUMMARIES_PER_PAGE));
                const page = Math.min(summariesPage, totalPages);
                const start = (page - 1) * SUMMARIES_PER_PAGE;
                const pageRows = filtered.slice(start, start + SUMMARIES_PER_PAGE);
                return (
                <section className="admin-table-section">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                        <h4 style={{ margin: 0 }}>{t('Prethodni zbirni nalozi', 'Previous summaries')}</h4>
                        <div style={{ position: 'relative', minWidth: 240 }}>
                            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(0,0,0,0.45)', fontSize: '0.95rem' }}>🔍</span>
                            <input
                                type="search"
                                value={summariesQuery}
                                onChange={(e) => { setSummariesQuery(e.target.value); setSummariesPage(1); }}
                                placeholder={t('Pretraži ZN šifru, status, printao…', 'Search ZN code, status, printed by…')}
                                style={{
                                    width: '100%',
                                    padding: '6px 10px 6px 32px',
                                    borderRadius: 8,
                                    border: '1px solid rgba(var(--ad-soft-rgb),0.35)',
                                    fontSize: '0.86rem',
                                    background: '#fffdf6',
                                }}
                            />
                            {summariesQuery ? (
                                <button type="button" onClick={() => { setSummariesQuery(''); setSummariesPage(1); }} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 0, cursor: 'pointer', color: 'rgba(0,0,0,0.5)', fontSize: '1rem', padding: '2px 6px' }} aria-label={t('Očisti', 'Clear')}>×</button>
                            ) : null}
                        </div>
                    </div>
                    <table className="admin-user-table">
                        <thead><tr><th>{t('Kod', 'Code')}</th><th>{t('Datum', 'Date')}</th><th>{t('Naloga', 'Orders')}</th><th>{t('Artikala', 'Items')}</th><th>{t('Komada', 'Units')}</th><th>Status</th><th /></tr></thead>
                        <tbody>
                            {pageRows.length === 0 ? (
                                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'rgba(0,0,0,0.5)', padding: '1rem', fontStyle: 'italic' }}>{q ? t('Nema rezultata za pretragu.', 'No matches.') : t('Nema zbirnih naloga.', 'No summaries yet.')}</td></tr>
                            ) : null}
                            {pageRows.map((s) => (
                                <tr key={s.id}>
                                    <td><strong>{s.public_code}</strong></td>
                                    <td>{new Date(s.created_at).toLocaleString('hr-HR')}</td>
                                    <td>{s.orders_count}</td>
                                    <td>{s.distinct_products}</td>
                                    <td>{s.total_units}</td>
                                    <td><span style={{ color: s.status === 'open' ? '#1f7a36' : s.status === 'printed' ? 'var(--ad-ink)' : '#b14040' }}>{s.status}</span></td>
                                    <td style={{ display: 'flex', gap: 4 }}>
                                        <button type="button" className="secondary compact" onClick={async () => {
                                            try {
                                                const r = await api<{ summary: Summary; orders: any[]; items: any[] }>(`/admin/warehouse-summaries/${s.id}`, token);
                                                setDrilldown(r);
                                            } catch (e) { setError(e instanceof Error ? e.message : ''); }
                                        }}>{t('Detalji', 'Details')}</button>
                                        <button type="button" className="secondary compact" onClick={async () => {
                                            try {
                                                const res = await fetch(`/api/admin/warehouse-summaries/${s.id}/print`, { headers: { Authorization: `Bearer ${token}` } });
                                                if (! res.ok) throw new Error(`HTTP ${res.status}`);
                                                const blob = await res.blob();
                                                const url = URL.createObjectURL(blob);
                                                window.open(url, '_blank', 'noopener,noreferrer');
                                                // Revoke nakon malo vremena (Chromium ponekad treba live URL na novom tabu)
                                                setTimeout(() => URL.revokeObjectURL(url), 60_000);
                                            } catch (e) { setError(e instanceof Error ? e.message : 'PDF download fail'); }
                                        }}>🖨 PDF</button>
                                        {s.status === 'open' ? (
                                            <>
                                                <button type="button" className="secondary compact" onClick={async () => {
                                                    try { await api(`/admin/warehouse-summaries/${s.id}/mark-printed`, token, { method: 'POST' }); await reload(); } catch (e) { setError(e instanceof Error ? e.message : ''); }
                                                }}>{t('Označi kao printano', 'Mark printed')}</button>
                                                <button type="button" className="secondary compact" style={{ color: '#b14040' }} onClick={async () => {
                                                    if (! confirm(t('Poništiti zbirni nalog? Pojedinačni se vraćaju u Neobrađene.', 'Cancel summary? Individual orders return to pending.'))) return;
                                                    try { await api(`/admin/warehouse-summaries/${s.id}/cancel`, token, { method: 'POST' }); await reload(); } catch (e) { setError(e instanceof Error ? e.message : ''); }
                                                }}>{t('Poništi', 'Cancel')}</button>
                                            </>
                                        ) : null}
                                        {s.status === 'printed' && isSuperadmin ? (
                                            <button type="button" className="secondary compact" style={{ color: 'var(--ad-ink)' }} onClick={async () => {
                                                if (! confirm(t('Vratiti zbirni nalog iz printanog u otvoreno stanje?', 'Revert summary from printed to open?'))) return;
                                                try { await api(`/admin/warehouse-summaries/${s.id}/unmark-printed`, token, { method: 'POST' }); await reload(); } catch (e) { setError(e instanceof Error ? e.message : ''); }
                                            }}>↺ {t('Označi kao neprintano', 'Unmark printed')}</button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filtered.length > SUMMARIES_PER_PAGE ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.6rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.82rem', color: 'rgba(0,0,0,0.6)' }}>
                                {t('Prikazano', 'Showing')} <strong>{start + 1}</strong>–<strong>{Math.min(start + SUMMARIES_PER_PAGE, filtered.length)}</strong> {t('od', 'of')} <strong>{filtered.length}</strong>{q ? ` (${t('filtrirano iz', 'filtered from')} ${summaries.length})` : ''}
                            </span>
                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                                <button type="button" className="secondary compact" disabled={page <= 1} onClick={() => setSummariesPage(1)} style={{ opacity: page <= 1 ? 0.4 : 1 }}>«</button>
                                <button type="button" className="secondary compact" disabled={page <= 1} onClick={() => setSummariesPage((p) => Math.max(1, p - 1))} style={{ opacity: page <= 1 ? 0.4 : 1 }}>‹</button>
                                <span style={{ fontSize: '0.84rem', padding: '0 0.5rem', color: 'var(--ad-ink)', fontWeight: 600 }}>
                                    {page} / {totalPages}
                                </span>
                                <button type="button" className="secondary compact" disabled={page >= totalPages} onClick={() => setSummariesPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>›</button>
                                <button type="button" className="secondary compact" disabled={page >= totalPages} onClick={() => setSummariesPage(totalPages)} style={{ opacity: page >= totalPages ? 0.4 : 1 }}>»</button>
                            </div>
                        </div>
                    ) : null}
                </section>
                );
            })() : null}

            {/* F2 — roba na putu. Oznaka otpreme/zaprimanja je jedino mjesto gdje
                nalog ulazi u S3 (064 -> poslovnica) ili T1 (poslovnica -> 064). */}
            <section style={{ marginTop: '1.5rem', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: '0.8rem 1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <h4 style={{ margin: 0 }}>{t('Roba na putu', 'Goods in transit')}</h4>
                    <button type="button" className="secondary compact" onClick={() => setShowTransit((v) => !v)}>
                        {showTransit ? t('Sakrij', 'Hide') : t('Prikaži', 'Show')}
                    </button>
                    {showTransit && transit ? (
                        <span className="muted" style={{ fontSize: '0.82rem' }}>
                            S3 <strong>{transit.outbound.qty}</strong> {t('kom', 'pcs')} · T1 <strong>{transit.inbound.qty}</strong> {t('kom', 'pcs')}
                        </span>
                    ) : null}
                </div>

                {showTransit ? (
                    <>
                        {transitBusy ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
                        {transit ? (
                            <table className="admin-user-table" style={{ marginTop: '0.6rem', maxWidth: 720 }}>
                                <thead><tr><th>{t('Smjer', 'Direction')}</th><th style={{ textAlign: 'right' }}>{t('Komada', 'Units')}</th><th style={{ textAlign: 'right' }}>{t('Redaka', 'Rows')}</th><th>{t('Po sloju', 'By layer')}</th></tr></thead>
                                <tbody>
                                    <tr>
                                        <td>{t('S3 · veleprodaja → poslovnice', 'S3 · wholesale → branches')}</td>
                                        <td style={{ textAlign: 'right' }}><strong>{transit.outbound.qty}</strong></td>
                                        <td style={{ textAlign: 'right' }}>{transit.outbound.count}</td>
                                        <td style={{ fontSize: '0.8rem' }}>{Object.entries(transit.outbound.by_layer).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('T1 · poslovnice → veleprodaja', 'T1 · branches → wholesale')}</td>
                                        <td style={{ textAlign: 'right' }}><strong>{transit.inbound.qty}</strong></td>
                                        <td style={{ textAlign: 'right' }}>{transit.inbound.count}</td>
                                        <td style={{ fontSize: '0.8rem' }}>{Object.entries(transit.inbound.by_layer).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td>{t('Faros (bez smjera)', 'Faros (no direction)')} {transit.reconcile.snapshot_date ?? '—'}</td>
                                        <td style={{ textAlign: 'right' }}>{transit.reconcile.faros_qty_in_transit ?? '—'}</td>
                                        <td />
                                        <td style={{ fontSize: '0.8rem' }}>
                                            {transit.reconcile.difference === null ? '—' : `${t('razlika BIS − Faros', 'diff BIS − Faros')}: ${transit.reconcile.difference}`}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : null}

                        <div style={{ display: 'flex', gap: 6, marginTop: '0.8rem' }}>
                            <button type="button" className={transitTab === 'not_shipped' ? 'compact' : 'secondary compact'} onClick={() => setTransitTab('not_shipped')}>
                                {t('Za otpremu', 'To ship')}
                            </button>
                            <button type="button" className={transitTab === 'on_the_way' ? 'compact' : 'secondary compact'} onClick={() => setTransitTab('on_the_way')}>
                                {t('Na putu', 'On the way')}
                            </button>
                        </div>

                        <div className="table-wrap" style={{ marginTop: '0.5rem' }}>
                            <table className="admin-user-table">
                                <thead><tr><th>{t('Šifra', 'Code')}</th><th>{t('Datum', 'Date')}</th><th>{t('Iz', 'From')}</th><th>{t('U', 'To')}</th><th style={{ textAlign: 'right' }}>{t('Komada', 'Units')}</th><th>{t('Otpremljeno', 'Shipped')}</th><th /></tr></thead>
                                <tbody>
                                    {transitOrders.map((o) => (
                                        <tr key={o.id}>
                                            <td><strong>{o.public_code}</strong></td>
                                            <td>{o.document_date ?? '—'}</td>
                                            <td>{o.warehouse_from_code} {o.warehouse_from_name}</td>
                                            <td>{o.warehouse_to_code} {o.warehouse_to_name}</td>
                                            <td style={{ textAlign: 'right' }}>{o.total_units}</td>
                                            <td style={{ fontSize: '0.8rem' }}>{o.shipped_at ? `${o.shipped_at} · ${o.shipped_by_name ?? ''}` : '—'}</td>
                                            <td style={{ display: 'flex', gap: 4 }}>
                                                {o.shipped_at ? (
                                                    <button type="button" className="secondary compact" onClick={() => void markTransit(o.id, 'mark-received')}>
                                                        {t('Zaprimljeno', 'Received')}
                                                    </button>
                                                ) : (
                                                    <button type="button" className="secondary compact" onClick={() => void markTransit(o.id, 'mark-shipped')}>
                                                        {t('Otpremljeno', 'Shipped')}
                                                    </button>
                                                )}
                                                {isSuperadmin && o.shipped_at ? (
                                                    <button type="button" className="secondary compact" title={t('Poništi oznaku', 'Undo mark')} onClick={() => void markTransit(o.id, 'undo-transit')}>↩</button>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                    {transitOrders.length === 0 && ! transitBusy ? (
                                        <tr><td colSpan={7} className="muted">{t('Nema naloga u ovoj skupini.', 'No orders in this group.')}</td></tr>
                                    ) : null}
                                </tbody>
                            </table>
                        </div>
                    </>
                ) : null}
            </section>

            <h4 style={{ marginTop: '1.5rem' }}>{t('Neobrađeni nalozi', 'Pending orders')} ({pending.length})</h4>
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            <div className="table-wrap">
                <table className="admin-user-table">
                    <thead><tr><th /><th>{t('Šifra', 'Code')}</th><th>{t('Datum', 'Date')}</th><th>{t('Poslovnica', 'Branch')}</th><th>{t('Iznos', 'Amount')}</th><th>{t('Artikala', 'Items')}</th><th>{t('Komada', 'Units')}</th><th>{t('Izvor', 'Source')}</th><th /><th /></tr></thead>
                    <tbody>
                        {pending.map((o) => (
                            <React.Fragment key={o.id}>
                            <tr style={o.has_errors ? { background: 'rgba(177,64,64,0.04)' } : undefined}>
                                <td><input type="checkbox" checked={!! selected[o.id]} onChange={(e) => setSelected({ ...selected, [o.id]: e.target.checked })} /></td>
                                <td><strong>{o.public_code}</strong>{o.has_errors ? <span title={o.errors ?? ''} style={{ marginLeft: 6, color: '#b14040', fontWeight: 700 }}>⚠</span> : null}</td>
                                <td>{o.document_date ?? '—'}</td>
                                <td>{o.warehouse_to_code} {o.warehouse_to_name}</td>
                                <td style={{ textAlign: 'right' }}>{formatMoney(o.total_amount_eur, locale)}</td>
                                <td style={{ textAlign: 'right' }}>{o.distinct_products}</td>
                                <td style={{ textAlign: 'right' }}>{o.total_units}</td>
                                <td>{o.source === 'email' ? '📧' : '⬆️'}</td>
                                <td style={{ display: 'flex', gap: 4 }}>
                                    <button
                                        type="button"
                                        className="secondary compact"
                                        title={t('Detalji izvora (kako i kada je dospjelo)', 'Source details (how & when it arrived)')}
                                        onClick={() => setExpandedOrderId((cur) => cur === o.id ? null : o.id)}
                                        style={{ padding: '2px 8px' }}
                                    >ⓘ</button>
                                    <button type="button" className="secondary compact" onClick={async () => { try { const r = await api(`/admin/warehouse-orders/${o.id}`, token); setOrderDetail(r); } catch (e) { setError(e instanceof Error ? e.message : ''); } }}>{t('Detalji', 'Details')}</button>
                                    <button type="button" className="secondary compact" style={{ color: '#b14040' }} title={t('Trajno obriši nalog', 'Permanently delete order')} onClick={async () => {
                                        if (! confirm(t(`Trajno obrisati nalog ${o.public_code}? Ovo se NE može poništiti.`, `Permanently delete order ${o.public_code}? This cannot be undone.`))) return;
                                        try {
                                            await api(`/admin/warehouse-orders/${o.id}`, token, { method: 'DELETE' });
                                            await reload();
                                        } catch (e) { setError(e instanceof Error ? e.message : ''); }
                                    }}>🗑</button>
                                </td>
                                <td>{o.has_errors ? <span style={{ color: '#b14040', fontSize: '0.78rem' }}>⚠ {t('Greške', 'Errors')}</span> : <span style={{ color: '#1f7a36', fontSize: '0.78rem' }}>✓ {t('OK', 'OK')}</span>}</td>
                            </tr>
                            {expandedOrderId === o.id ? (
                                <tr>
                                    <td colSpan={10} style={{ background: 'rgba(var(--ad-soft-rgb),0.08)', borderTop: 0, padding: '10px 14px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px 18px', fontSize: '0.82rem', color: 'rgba(0,0,0,0.78)' }}>
                                            <div>
                                                <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Izvor', 'Source')}</span>
                                                <div><strong>{o.source === 'email' ? '📧 ' + t('Email (preskladistenje@aurodomus.eu)', 'Email (preskladistenje@aurodomus.eu)') : '⬆️ ' + t('Mass upload', 'Mass upload')}</strong></div>
                                            </div>
                                            {o.source_filename ? (
                                                <div>
                                                    <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Datoteka / privitak', 'File / attachment')}</span>
                                                    <div style={{ wordBreak: 'break-all' }}><strong>📎 {o.source_filename}</strong></div>
                                                </div>
                                            ) : null}
                                            <div>
                                                <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Uvezeno', 'Imported at')}</span>
                                                <div><strong>📅 {new Date(o.created_at.replace(' ', 'T')).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong></div>
                                            </div>
                                            {o.order_number ? (
                                                <div>
                                                    <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Broj narudžbe (ERP)', 'Order number (ERP)')}</span>
                                                    <div><strong>🔗 {o.order_number}</strong></div>
                                                </div>
                                            ) : null}
                                            {o.warehouse_from_code || o.warehouse_from_name ? (
                                                <div>
                                                    <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{t('Iz skladišta', 'From warehouse')}</span>
                                                    <div><strong>🏭 {o.warehouse_from_code} {o.warehouse_from_name}</strong></div>
                                                </div>
                                            ) : null}
                                            {o.has_errors && o.errors ? (
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <span className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#b14040' }}>{t('Greške', 'Errors')}</span>
                                                    <pre style={{ margin: '4px 0 0', fontSize: '0.74rem', color: '#b14040', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{o.errors}</pre>
                                                </div>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            ) : null}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {showUpload ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={async () => { setShowUpload(false); await reload(); }}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(640px, 96vw)' }}>
                        <h4 style={{ marginTop: 0 }}>{t('Mass upload PDF naloga', 'Mass upload PDF orders')}</h4>
                        <MassUploadForm token={token} locale={locale} onDone={async () => { setShowUpload(false); await reload(); }} />
                        <button type="button" className="secondary compact" style={{ marginTop: 12 }} onClick={async () => { setShowUpload(false); await reload(); }}>{t('Zatvori', 'Close')}</button>
                    </div>
                </div>
            ) : null}

            {drilldown ? (
                <SummaryDrilldownModal
                    drilldown={drilldown}
                    locale={locale}
                    token={token}
                    isSuperadmin={isSuperadmin}
                    onClose={() => setDrilldown(null)}
                    onReload={async () => { setDrilldown(null); await reload(); }}
                    onError={setError}
                />
            ) : null}
            {orderDetail ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setOrderDetail(null)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ width: 'min(900px, 96vw)' }}>
                        <h4 style={{ marginTop: 0 }}>{orderDetail.order.public_code}</h4>
                        <p className="muted">{orderDetail.order.warehouse_from_name} → {orderDetail.order.warehouse_to_name} · {orderDetail.order.document_date}</p>
                        {orderDetail.order.has_errors ? (
                            <div style={{ background: 'rgba(177,64,64,0.08)', border: '1px solid rgba(177,64,64,0.25)', padding: 8, borderRadius: 6, fontSize: '0.82rem', color: '#b14040', marginBottom: 8 }}>
                                <strong>⚠ Greške:</strong>
                                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                    {(JSON.parse(orderDetail.order.errors ?? '[]') as string[]).map((e: string, i: number) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        ) : null}
                        <table className="admin-user-table">
                            <thead><tr><th>L</th><th>{t('Šifra PDF', 'SKU')}</th><th>{t('Naziv', 'Name')}</th><th style={{ textAlign: 'right' }}>{t('Kom', 'Qty')}</th><th>Match</th><th>sim%</th></tr></thead>
                            <tbody>
                                {orderDetail.items.map((it: any) => (
                                    <tr key={it.id} style={it.match_status !== 'matched' ? { background: 'rgba(177,64,64,0.04)' } : undefined}>
                                        <td>{it.line_no}</td>
                                        <td><strong>{it.sku_from_pdf}</strong></td>
                                        <td>{it.name_from_pdf}<br /><span style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }}>{it.product_name_db ?? 'nema u bazi'}</span></td>
                                        <td style={{ textAlign: 'right' }}>{it.quantity}</td>
                                        <td>{it.match_status === 'matched' ? '✓' : it.match_status === 'sku_mismatch' ? '⚠ šifra' : '⚠ ime'}</td>
                                        <td>{it.name_similarity != null ? Math.round(it.name_similarity) + '%' : '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button type="button" className="secondary compact" style={{ marginTop: 12 }} onClick={() => setOrderDetail(null)}>{t('Zatvori', 'Close')}</button>
                    </div>
                </div>
            ) : null}
        </section>
    );
}

function MassUploadForm({ token, locale, onDone }: { token: string; locale: Locale; onDone: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [dragOver, setDragOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const [busyLabel, setBusyLabel] = useState('');
    const [results, setResults] = useState<Array<{ file: string; status: string; order_id?: number; errors?: string[]; items_count?: number; error?: string }>>([]);
    const [autoCloseIn, setAutoCloseIn] = useState<number | null>(null);

    const addFiles = (incoming: FileList | File[]) => {
        const arr = Array.from(incoming).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
        // Dedupe by name+size
        const existing = new Set(files.map((f) => `${f.name}|${f.size}`));
        const added = arr.filter((f) => !existing.has(`${f.name}|${f.size}`));
        setFiles([...files, ...added]);
    };

    const totalBytes = files.reduce((s, f) => s + f.size, 0);
    const fmtSize = (b: number) => b > 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + ' MB' : (b / 1024).toFixed(0) + ' KB';

    // Auto-close countdown nakon uspješnog upload-a (samo ako nema failures)
    useEffect(() => {
        if (autoCloseIn == null) return;
        if (autoCloseIn <= 0) { onDone(); return; }
        const id = window.setTimeout(() => setAutoCloseIn((v) => (v == null ? v : v - 1)), 1000);
        return () => window.clearTimeout(id);
    }, [autoCloseIn, onDone]);

    return (
        <div style={{ position: 'relative' }}>
            {/* Spinner overlay tijekom upload-a */}
            {busy ? (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255,251,235,0.92)',
                    backdropFilter: 'blur(2px)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '0.8rem',
                    borderRadius: 12,
                }}>
                    <BrandSpinner label={busyLabel || t('Učitavam…', 'Uploading…')} size="lg" />
                    <p style={{ margin: 0, fontSize: '0.82rem', color: 'rgba(107,79,28,0.75)' }}>
                        {t('Molim ne zatvaraj prozor.', 'Please do not close this window.')}
                    </p>
                </div>
            ) : null}

            {/* Drag and drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files) addFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                role="button"
                tabIndex={0}
                style={{
                    border: dragOver ? '2.5px dashed var(--ad-acc)' : '2.5px dashed rgba(var(--ad-soft-rgb),0.45)',
                    background: dragOver ? 'rgba(var(--ad-acc-rgb),0.10)' : 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
                    borderRadius: 16,
                    padding: '2.2rem 1.5rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 150ms ease',
                    marginBottom: '1rem',
                }}
            >
                <div style={{ fontSize: '3rem', lineHeight: 1, marginBottom: '0.5rem' }}>📤</div>
                <p style={{ margin: '0 0 0.3rem', fontSize: '1.05rem', fontWeight: 600, color: 'var(--ad-ink)' }}>
                    {dragOver ? t('Otpusti PDF-ove ovdje', 'Drop PDFs here') : t('Povuci i ispusti PDF dokumente ovdje', 'Drag & drop PDF files here')}
                </p>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(0,0,0,0.55)' }}>
                    {t('ili klikni za odabir s diska', 'or click to browse')} · <strong>PDF</strong>, {t('više datoteka odjednom', 'multiple files at once')}
                </p>
                <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.currentTarget.value = ''; }}
                />
            </div>

            {/* File list preview */}
            {files.length > 0 ? (
                <div style={{ marginBottom: '1rem', padding: '0.6rem 0.8rem', background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.2)', borderRadius: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <strong style={{ color: 'var(--ad-ink)' }}>{t('Spremno za upload', 'Ready to upload')}: {files.length} {files.length === 1 ? t('datoteka', 'file') : t('datoteka', 'files')}</strong>
                        <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.55)' }}>{fmtSize(totalBytes)}</span>
                    </div>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 220, overflowY: 'auto' }}>
                        {files.map((f, idx) => (
                            <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: '0.85rem', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                <span style={{ fontSize: '1.1rem' }}>📄</span>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                                <span style={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.75rem' }}>{fmtSize(f.size)}</span>
                                <button type="button" onClick={() => setFiles(files.filter((_, i) => i !== idx))} style={{ background: 'transparent', border: 0, color: '#b14040', cursor: 'pointer', fontWeight: 700, fontSize: '1.1rem' }} aria-label="remove">×</button>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    className="compact"
                    disabled={busy || files.length === 0}
                    style={{
                        background: files.length === 0 ? '#e5e7eb' : 'linear-gradient(135deg, var(--ad-acc), #b8895a)',
                        color: '#fff',
                        padding: '8px 18px',
                        fontWeight: 600,
                        fontSize: '0.92rem',
                        border: 0,
                        borderRadius: 8,
                        cursor: files.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: busy ? 0.7 : 1,
                    }}
                    onClick={async () => {
                        if (files.length === 0) return;
                        setBusy(true);
                        setBusyLabel(t(
                            `Učitavam ${files.length} ${files.length === 1 ? 'PDF' : 'PDF-ova'}…`,
                            `Uploading ${files.length} ${files.length === 1 ? 'PDF' : 'PDFs'}…`,
                        ));
                        const fd = new FormData();
                        files.forEach((f) => fd.append('files[]', f));
                        try {
                            const res = await fetch('/api/admin/warehouse-orders/upload', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                            const data = await res.json();
                            const rows: Array<{ file: string; status: string; order_id?: number; errors?: string[]; items_count?: number; error?: string }> = data.results ?? [];
                            setResults(rows);
                            setFiles([]);
                            const hasFailures = rows.some((r) => r.status === 'failed');
                            if (! hasFailures && rows.length > 0) {
                                // Sve OK → countdown za auto-close (3s)
                                setAutoCloseIn(3);
                            }
                        } catch (e) {
                            setResults([{ file: '?', status: 'failed', error: e instanceof Error ? e.message : 'upload fail' }]);
                        } finally {
                            setBusy(false);
                            setBusyLabel('');
                        }
                    }}
                >
                    {busy ? `⏳ ${t('Učitavam…', 'Uploading…')}` : `📥 ${t('Učitaj sve', 'Upload all')}${files.length > 0 ? ` (${files.length})` : ''}`}
                </button>
                {files.length > 0 && ! busy ? (
                    <button type="button" className="secondary compact" onClick={() => setFiles([])}>{t('Očisti', 'Clear')}</button>
                ) : null}
                {results.length > 0 ? <button type="button" className="secondary compact" onClick={onDone} style={{ marginLeft: 'auto' }}>{t('Gotovo', 'Done')}</button> : null}
            </div>

            {/* Results */}
            {results.length > 0 ? (
                <div style={{ marginTop: '1rem' }}>
                    {/* Auto-close countdown banner */}
                    {autoCloseIn != null ? (
                        <div style={{
                            padding: '8px 12px',
                            marginBottom: 10,
                            borderRadius: 8,
                            background: 'rgba(31,122,54,0.10)',
                            border: '1px solid rgba(31,122,54,0.28)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            fontSize: '0.86rem',
                            color: '#1f7a36',
                        }}>
                            <span style={{ fontSize: '1.1rem' }}>✓</span>
                            <strong style={{ flex: 1 }}>
                                {t(
                                    `Sve uspješno učitano. Zatvaram za ${autoCloseIn}s i osvježavam listu…`,
                                    `All uploads complete. Closing in ${autoCloseIn}s and refreshing…`,
                                )}
                            </strong>
                            <button type="button" className="secondary compact" onClick={() => setAutoCloseIn(null)}>{t('Ostavi otvoreno', 'Keep open')}</button>
                            <button type="button" className="compact" style={{ background: '#1f7a36', color: '#fff', border: 0 }} onClick={onDone}>{t('Zatvori odmah', 'Close now')}</button>
                        </div>
                    ) : null}
                    <div style={{ display: 'flex', gap: '0.6rem', marginBottom: 8, fontSize: '0.85rem' }}>
                        <strong>{t('Rezultati', 'Results')}:</strong>
                        <span style={{ color: '#1f7a36' }}>✓ {results.filter((r) => r.status === 'clean').length} {t('čisto', 'clean')}</span>
                        <span style={{ color: '#92400e' }}>⚠ {results.filter((r) => r.status === 'with_errors').length} {t('s greškama', 'with errors')}</span>
                        <span style={{ color: 'var(--ad-ink)' }}>↻ {results.filter((r) => r.status === 'duplicate').length} {t('duplikati', 'duplicates')}</span>
                        <span style={{ color: '#b14040' }}>✗ {results.filter((r) => r.status === 'failed').length} {t('neuspjeh', 'failed')}</span>
                    </div>
                    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                        {results.map((r, i) => {
                            const bg = r.status === 'clean' ? 'rgba(31,122,54,0.08)' : r.status === 'duplicate' ? 'rgba(var(--ad-soft-rgb),0.12)' : r.status === 'with_errors' ? 'rgba(254,243,199,0.6)' : 'rgba(177,64,64,0.08)';
                            const icon = r.status === 'clean' ? '✓' : r.status === 'duplicate' ? '↻' : r.status === 'with_errors' ? '⚠' : '✗';
                            return (
                                <div key={i} style={{ padding: '8px 12px', background: bg, borderRadius: 6, marginBottom: 6, fontSize: '0.86rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{icon}</span>
                                        <strong style={{ flex: 1 }}>{r.file}</strong>
                                        <span style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)' }}>{r.status}{r.items_count != null ? ` · ${r.items_count} stavki` : ''}</span>
                                    </div>
                                    {r.errors && r.errors.length > 0 ? (
                                        <ul style={{ margin: '4px 0 0 22px', padding: 0, color: '#92400e', fontSize: '0.78rem' }}>
                                            {r.errors.slice(0, 3).map((e, j) => <li key={j}>{e}</li>)}
                                            {r.errors.length > 3 ? <li>… +{r.errors.length - 3}</li> : null}
                                        </ul>
                                    ) : null}
                                    {r.error ? <p style={{ margin: '4px 0 0 22px', color: '#b14040', fontSize: '0.78rem' }}>{r.error}</p> : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

type DrilldownData = { summary: Summary; orders: any[]; items: any[] };

function SummaryDrilldownModal({ drilldown, locale, token, isSuperadmin, onClose, onReload, onError }: {
    drilldown: DrilldownData;
    locale: Locale;
    token: string;
    isSuperadmin: boolean;
    onClose: () => void;
    onReload: () => Promise<void> | void;
    onError: (msg: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const totalUnits = drilldown.items.reduce((s: number, it: any) => s + Number(it.total_quantity ?? 0), 0);
    const maxItemQty = drilldown.items.reduce((m: number, it: any) => Math.max(m, Number(it.total_quantity ?? 0)), 0);
    const status = (drilldown.summary as any).status ?? 'open';
    const statusBg = status === 'open' ? 'rgba(31,122,54,0.18)' : status === 'printed' ? 'rgba(var(--ad-soft-rgb),0.22)' : 'rgba(177,64,64,0.18)';
    const statusLabel = status === 'open' ? t('Otvoren', 'Open') : status === 'printed' ? t('Printano', 'Printed') : t('Poništen', 'Cancelled');
    const fmtQty = (n: number) => Number.isInteger(n) ? n.toString() : n.toFixed(2).replace('.', ',');
    let createdDate = '—';
    try {
        const ts = (drilldown.summary as any).created_at;
        if (ts) createdDate = new Date(ts.replace(' ', 'T')).toLocaleString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { /* swallow */ }

    return (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 'min(1080px, 96vw)',
                maxHeight: '94vh',
                overflowY: 'auto',
                background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                borderRadius: 18,
                boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                    padding: '1.4rem 1.8rem',
                    color: '#fffbeb',
                }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 240 }}>
                            <p style={{ margin: 0, fontSize: '0.74rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>
                                {t('Zbirni nalog preskladištenja', 'Stock transfer summary order')}
                            </p>
                            <h2 style={{ margin: '0.25rem 0 0', fontSize: '1.7rem', fontWeight: 700, letterSpacing: '-0.01em' }}>
                                {drilldown.summary.public_code}
                            </h2>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    background: statusBg, color: '#fffbeb',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    padding: '3px 10px', borderRadius: 12,
                                    fontSize: '0.74rem', fontWeight: 600,
                                    letterSpacing: '0.04em', textTransform: 'uppercase',
                                }}>● {statusLabel}</span>
                                <span style={{ fontSize: '0.82rem', color: 'rgba(255,251,235,0.85)' }}>🕒 {createdDate}</span>
                                {(drilldown.summary as any).printed_at ? (
                                    <span style={{ fontSize: '0.82rem', color: 'rgba(255,251,235,0.85)' }}>
                                        🖨 {t('printao', 'printed by')} {(drilldown.summary as any).printed_by_name || '—'}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button type="button" onClick={async () => {
                                try {
                                    const res = await fetch(`/api/admin/warehouse-summaries/${drilldown.summary.id}/print`, { headers: { Authorization: `Bearer ${token}` } });
                                    if (! res.ok) throw new Error(`HTTP ${res.status}`);
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                                } catch (e) { onError(e instanceof Error ? e.message : 'PDF fail'); }
                            }} style={{
                                background: 'rgba(255,255,255,0.18)', color: '#fffbeb',
                                border: '1px solid rgba(255,255,255,0.35)',
                                padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
                                fontWeight: 600, fontSize: '0.85rem',
                            }}>🖨 PDF</button>
                            {status === 'open' ? (
                                <>
                                    <button type="button" onClick={async () => {
                                        try {
                                            await api(`/admin/warehouse-summaries/${drilldown.summary.id}/mark-printed`, token, { method: 'POST' });
                                            await onReload();
                                        } catch (e) { onError(e instanceof Error ? e.message : ''); }
                                    }} style={{
                                        background: '#1f7a36', color: '#fff', border: 0,
                                        padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
                                        fontWeight: 600, fontSize: '0.85rem',
                                        boxShadow: '0 2px 8px rgba(31,122,54,0.3)',
                                    }}>✓ {t('Označi printano', 'Mark printed')}</button>
                                    <button type="button" onClick={async () => {
                                        if (! confirm(t('Poništiti zbirni nalog?', 'Cancel summary?'))) return;
                                        try {
                                            await api(`/admin/warehouse-summaries/${drilldown.summary.id}/cancel`, token, { method: 'POST' });
                                            await onReload();
                                        } catch (e) { onError(e instanceof Error ? e.message : ''); }
                                    }} style={{
                                        background: 'rgba(177,64,64,0.85)', color: '#fff',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
                                        fontWeight: 600, fontSize: '0.85rem',
                                    }}>✗ {t('Poništi', 'Cancel')}</button>
                                </>
                            ) : null}
                            {status === 'printed' && isSuperadmin ? (
                                <button type="button" onClick={async () => {
                                    if (! confirm(t('Vratiti zbirni nalog iz printanog u otvoreno stanje?', 'Revert summary from printed to open?'))) return;
                                    try {
                                        await api(`/admin/warehouse-summaries/${drilldown.summary.id}/unmark-printed`, token, { method: 'POST' });
                                        await onReload();
                                    } catch (e) { onError(e instanceof Error ? e.message : ''); }
                                }} style={{
                                    background: 'rgba(255,255,255,0.22)', color: '#fffbeb',
                                    border: '1px solid rgba(255,255,255,0.4)',
                                    padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
                                    fontWeight: 600, fontSize: '0.85rem',
                                }}>↺ {t('Označi neprintano', 'Unmark printed')}</button>
                            ) : null}
                            <button type="button" onClick={onClose} style={{
                                background: 'transparent', color: '#fffbeb', border: 0,
                                fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1,
                                padding: '4px 8px',
                            }} aria-label={t('Zatvori', 'Close')}>×</button>
                        </div>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '0.9rem',
                    padding: '1.2rem 1.8rem 0.6rem',
                }}>
                    {[
                        { label: t('Sadržanih naloga', 'Included orders'), value: drilldown.orders.length, icon: '📋', color: 'var(--ad-ink)' },
                        { label: t('Različitih artikala', 'Distinct items'), value: drilldown.items.length, icon: '💎', color: 'var(--ad-acc)' },
                        { label: t('Ukupno komada', 'Total units'), value: fmtQty(totalUnits), icon: '🔢', color: '#1f7a36' },
                        { label: t('Poslovnica', 'Branches'), value: new Set(drilldown.orders.map((o: any) => o.warehouse_to_code).filter(Boolean)).size, icon: '🏬', color: '#3b6ea5' },
                    ].map((k, idx) => (
                        <div key={idx} style={{
                            background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.22)',
                            borderRadius: 12, padding: '0.9rem 1rem',
                            boxShadow: '0 1px 3px rgba(107,79,28,0.05)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                                <span style={{ fontSize: '1.3rem' }}>{k.icon}</span>
                                <span style={{ fontSize: '0.72rem', color: 'rgba(0,0,0,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k.label}</span>
                            </div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 700, color: k.color, lineHeight: 1.1 }}>{k.value}</div>
                        </div>
                    ))}
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(260px, 1fr) minmax(420px, 1.4fr)',
                    gap: '1.2rem',
                    padding: '0.8rem 1.8rem 1.6rem',
                }}>
                    <div>
                        <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.92rem', color: 'var(--ad-ink)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            📋 {t('Sadržani nalozi', 'Included orders')}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {drilldown.orders.map((o: any) => (
                                <div key={o.id} style={{
                                    background: '#fff',
                                    border: '1px solid rgba(var(--ad-soft-rgb),0.18)',
                                    borderRadius: 10, padding: '0.7rem 0.85rem',
                                }}>
                                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--ad-ink)', marginBottom: 2 }}>{o.public_code}</div>
                                    <div style={{ fontSize: '0.78rem', color: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span>🏬</span>
                                        <span style={{ flex: 1 }}>{o.warehouse_to_name ?? '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.8rem', marginTop: 4, fontSize: '0.74rem', color: 'rgba(0,0,0,0.55)' }}>
                                        <span>📦 <strong style={{ color: '#1f7a36' }}>{fmtQty(Number(o.total_units ?? 0))}</strong> kom</span>
                                        {o.document_date ? <span>📅 {o.document_date}</span> : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h4 style={{ margin: '0 0 0.6rem', fontSize: '0.92rem', color: 'var(--ad-ink)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            💎 {t('Agregirane stavke', 'Aggregated items')}
                        </h4>
                        <div style={{
                            background: '#fff',
                            border: '1px solid rgba(var(--ad-soft-rgb),0.18)',
                            borderRadius: 12,
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '56px 1fr 130px',
                                padding: '0.55rem 0.9rem',
                                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                                borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                color: 'var(--ad-ink)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                            }}>
                                <span>{t('Šifra', 'SKU')}</span>
                                <span>{t('Naziv', 'Name')}</span>
                                <span style={{ textAlign: 'right' }}>{t('Količina', 'Quantity')}</span>
                            </div>
                            {drilldown.items.map((i: any, idx: number) => {
                                const qty = Number(i.total_quantity ?? 0);
                                const pct = maxItemQty > 0 ? (qty / maxItemQty) * 100 : 0;
                                return (
                                    <div key={`${i.sku}-${idx}`} style={{
                                        display: 'grid',
                                        gridTemplateColumns: '56px 1fr 130px',
                                        padding: '0.55rem 0.9rem',
                                        alignItems: 'center',
                                        borderBottom: idx < drilldown.items.length - 1 ? '1px solid rgba(var(--ad-soft-rgb),0.10)' : 'none',
                                        fontSize: '0.84rem',
                                        background: idx % 2 === 0 ? '#fff' : 'rgba(255,251,235,0.4)',
                                    }}>
                                        <span style={{
                                            fontFamily: 'monospace', fontWeight: 700, color: 'var(--ad-ink)',
                                            background: 'rgba(var(--ad-soft-rgb),0.10)', padding: '2px 6px',
                                            borderRadius: 5, display: 'inline-block',
                                            fontSize: '0.78rem', textAlign: 'center',
                                        }}>{i.sku ?? '—'}</span>
                                        <span style={{ paddingLeft: 8, color: '#1f2937' }}>{i.name ?? '—'}</span>
                                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                                            <strong style={{ color: '#1f7a36', fontSize: '0.95rem' }}>
                                                {fmtQty(qty)} <span style={{ fontWeight: 400, fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)' }}>{i.unit ?? 'kom'}</span>
                                            </strong>
                                            <div style={{ width: 100, height: 4, background: 'rgba(var(--ad-soft-rgb),0.15)', borderRadius: 2, overflow: 'hidden' }}>
                                                <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, var(--ad-acc), var(--ad-ink))', borderRadius: 2 }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div style={{
                    padding: '0.9rem 1.8rem 1.2rem',
                    borderTop: '1px solid rgba(var(--ad-soft-rgb),0.18)',
                    background: 'rgba(255,251,235,0.5)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.5rem',
                }}>
                    <button type="button" onClick={onClose} style={{
                        background: 'transparent', color: 'var(--ad-ink)',
                        border: '1px solid rgba(var(--ad-soft-rgb),0.4)',
                        padding: '8px 18px', borderRadius: 10, cursor: 'pointer',
                        fontWeight: 600, fontSize: '0.88rem',
                    }}>{t('Zatvori', 'Close')}</button>
                </div>
            </div>
        </div>
    );
}
